import React, { Key, useEffect, useState } from 'react';
import clsx from 'clsx'
import { useSelector } from 'react-redux'

import { ICodeVerificationFormProps, ICodeVerificationFormSendSmsCodeAgainProps, ICodeVerificationInputProps } from './ContractSigningPageProps'
import { TVerificationCodeInputsNamesModel, verificationCodeInputsNames } from 'store/ContractSignSlice/ContractSignModels'
import { resetVerificationCodeForm, setVerificationCodeInputValue, SMS_CODE_STATUSES } from 'store/ContractSignSlice/ContractSignSlice'
import ButtonCancelApplication from 'components/Buttons/ButtonCancelApplication/ButtonCancelApplication'
import Button, { AppButtonTypes } from 'components/Buttons/ButtonBase/Button'
import { submitSmsCodeAgainThunkAction } from 'store/ContractSignSlice/middleware/ContractSignThunkActions'
import useCodeVerificationInputLogic from './hooks/useCodeVerificationInputLogic'
import useSendSmsCodeAgainLogic from './hooks/useSendSmsCodeAgainLogic'
import useContractSigningPageLogic from './hooks/useContractSigningPageLogic'
import { changeApplicationStep } from 'store/ApplicationSlice/ApplicationSlice'
import { PAGE_STEPER_STAGES } from 'components/Navbar/pageSteps'
import useIsMobile from 'common/hooks/useIsMobile'
import Summary from 'components/Summary/Summary'
import useTextContents from 'common/hooks/useTextContents'
import { AppState } from 'store'

import './ContractSigningPage.scss'
import './safari.css'


const SMSCodeStatusText = () => {
   const contractSigningPageTextContents = useTextContents()
   const contractSignState = useSelector((state: AppState) => state.contractSignState)
   const { sendCodeSmsStatus } = contractSignState
   const [currentText, setCurrentText] = useState<string | null>()


   useEffect(() => {
   // Na podstawie obecnego statusu, zmieniamy na właściwy tekst
      switch(contractSignState.currentSmsCodeTextState){
         case SMS_CODE_STATUSES.CODE_SEND_CORRECTLY: {
            setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.codeResend.sendCorectly'))
            break
         }
         case SMS_CODE_STATUSES.LIMIT_REACHED: {
            setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.codeResend.limitReached'))
            break
         }
         case SMS_CODE_STATUSES.SEND_ERROR: {
            setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.codeResend.sendError'))
            break
         }
         case SMS_CODE_STATUSES.VALIDATION_ERROR_FULL_CODE_REQUIRED: {
            setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.validation.required'))
            break
         }
         case SMS_CODE_STATUSES.VALIDATION_ERROR_WRONG_CODE: {
            setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.validation.error'))
            break
         }
         case SMS_CODE_STATUSES.VALIDATION_ERROR_CLIENT_PESEL_RESERVED: {
            setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.validation.clientPeselReserved'))
            break;
         }
         default: {
            setCurrentText(null)
         }
      }
   }, [contractSignState.currentSmsCodeTextState])

 
   if(currentText === null || currentText?.length === 0){
      return null
   }

   if(sendCodeSmsStatus.hasError) {
      return (
         <p className="contract-signing-page-send-code-again-status-text contract-signing-page-send-code-again-error">{currentText}</p>
      )
   }

   return (
      <p className="contract-signing-page-send-code-again-status-text contract-signing-page-send-code-again-succes">{currentText}</p>
   )
}

/**
 * Komponent pojedynczego pola do wpisywania kodu sms. Ilość pól odpowiada aktualnej długości kodu sms
 */
const CodeVerificationInput = (props: ICodeVerificationInputProps): JSX.Element => {

   const { DATASET_INPUT_IDENTIFIER, handleOnClick, handleKeyDown, handleChange, handlePaste } = useCodeVerificationInputLogic(props)

   return (
      <input 
         name={props.name}
         value={props.value}
         id={props.id}
         maxLength={1} 
         autoComplete="off"
         inputMode='numeric'
         input-identifier={DATASET_INPUT_IDENTIFIER} //WARNING - custom attribute - nie zmieniać
         onChange={handleChange}
         onClick={handleOnClick}
         onKeyDown={handleKeyDown}
         onPaste={handlePaste}
         className='code-verification-input'
      />
   )
}

/**
 * Komponent obsługujący wyświetlanie statusów ponownej wysyłki kodu sms oraz błędów powstałych przy tej okazji.
 */
const CodeVerificationFormSendSmsCodeAgain = (props: ICodeVerificationFormSendSmsCodeAgainProps): JSX.Element  => {

   const { contractSignState: { sendCodeSmsStatus }, contractSigningPageTextContents  } = props
   const { canSendSmsAgain, setIsSended, setCanSendSmsAgain } = useSendSmsCodeAgainLogic()
 
   return (
      <div className='contract-signing-phone-code-verification-send-code-again-outer-wrapper'>
         <div className='contract-signing-phone-code-verification-send-code-again-button-wrapper'>
            <p>{contractSigningPageTextContents('contractSigningPage.smsCode.tipQuestion')}</p>
            <button
               disabled={!canSendSmsAgain || sendCodeSmsStatus.isLimit}
               onClick={() => {
                  props.dispatch(submitSmsCodeAgainThunkAction())
                  setIsSended(true)
                  setCanSendSmsAgain(false)
               }}
            >
               &nbsp;{contractSigningPageTextContents('contractSigningPage.smsCode.codeResend.header')}&nbsp;
            </button>
         </div>
         <SMSCodeStatusText />
      </div>
   )
}


/**
 * Komponent łączący wszystkie kontrolki do wpisywania kodu sms w formularz.
 */
const CodeVerificationForm = (props: ICodeVerificationFormProps): JSX.Element  => {

   const { contractSignState, contractSigningPageTextContents, dispatch } = props

   return (
      <div className='contract-signing-phone-code-verification-wrapper'>
         <h6 className='contract-signing-phone-code-heading'>{contractSigningPageTextContents('contractSigningPage.smsCode.header')}</h6>
         <form
            name="contract-signing-phone-code-verification"
            className={clsx(
               'contract-signing-phone-code-verification-inputs-wrapper', 
               'contract-signing-phone-code-verification-safari-only',
               (contractSignState.sendCodeSmsStatus.hasError) && 'contract-signing-form-has-error'
            )}
         >
            {
               Object.values(verificationCodeInputsNames).map((codeVerificationInputName: TVerificationCodeInputsNamesModel, index: Key) => {
                  return ( 
                     <CodeVerificationInput 
                        key={index} 
                        name={codeVerificationInputName}
                        handleSaveInputValueToState={(inputName: TVerificationCodeInputsNamesModel, newValue: string) => dispatch(setVerificationCodeInputValue({inputName, newValue}))}
                        id={index.toString()}
                        value={contractSignState.verificationCodeInputs[codeVerificationInputName]}
                        resetForm={() => dispatch(resetVerificationCodeForm())}
                     />
                  )
               })
            }
         </form>
         <CodeVerificationFormSendSmsCodeAgain dispatch={dispatch} contractSignState={contractSignState} contractSigningPageTextContents={contractSigningPageTextContents} />   
      </div>
   )
}


/**
 * Główny komponent strony z podpisaniem umowy, łączący pozostałe komponenty w jeden widok.
 */
const ContractSigningPage = (): JSX.Element => {

   const contractSigningPageTextContents = useTextContents()
   const isMobileDevice = useIsMobile()
   const { downloadAgreement, handleOnContractSigningSubmit, dispatch, contractSignState } = useContractSigningPageLogic()

   useEffect(() => {
      dispatch(changeApplicationStep(PAGE_STEPER_STAGES.CONTRACT_SIGNING))
   }, [])
   
   return (
      <div className='contract-signing-page-outer-wrapper'>
         <section className='contract-signing-page-inner-wrapper'>
            <h1 className='contract-signing-page-label'>{contractSigningPageTextContents('contractSigningPage.header')}</h1>
            <div className='contract-signing-page-description'>
               <p>
                  {contractSigningPageTextContents('contractSigningPage.mainContentTextUp.part1')}
                  <button className='contract-signing-page-download-agreement-button' onClick={downloadAgreement}>
                     <b>&nbsp;{contractSigningPageTextContents('contractSigningPage.mainContentTextUp.part2')}&nbsp;</b>
                  </button> 
               </p>
               <p>{contractSigningPageTextContents('contractSigningPage.mainContentTextUp.part3')}</p>
            </div>
            <div className='contract-signing-page-send-code-wrapper'>
               <CodeVerificationForm contractSignState={contractSignState} dispatch={dispatch} contractSigningPageTextContents={contractSigningPageTextContents} />
            </div>
            <Button uiButtonType={AppButtonTypes.PRIMARY} buttonContent={contractSigningPageTextContents('contractSigningPage.submitButtonText')} className='contract-signing-page-sign-application-button page-main-button-submission-button' onClick={() => handleOnContractSigningSubmit()}/>
            <ButtonCancelApplication className='contract-signing-page-cancel-application-button' />
         </section>
         {!isMobileDevice && <Summary isExtended={true} />}
      </div>
   )
}

export default ContractSigningPage